/** Pure formatting helpers shared by the Quant presentational components. */

export type ReturnTone = 'up' | 'down' | 'flat' | 'none'

/** `+4.62%` / `−1.37%` (true minus, U+2212); `—` for a missing value. */
export function formatReturn(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(2)}%`
}

/** Sign tone with a +/-0.005 dead zone, matching the handoff's `retColor`. */
export function returnTone(value: number | null | undefined): ReturnTone {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'none'
  if (value > 0.005) return 'up'
  if (value < -0.005) return 'down'
  return 'flat'
}

/** Tailwind text colour class for a tone on a dark (`ink`) surface. */
export const TONE_CLASS: Record<ReturnTone, string> = {
  up: 'text-accent-line',
  down: 'text-alert',
  flat: 'text-paper-50',
  none: 'text-paper-40',
}

/** `45s`, `3m 12s`, `2h 5m`, `1d 4h` from a number of milliseconds (handoff `fmtDur`). */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—'
  const s = Math.max(0, Math.round(ms / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`
}

/** `2026-10-02 14:05` in the operator's local zone (handoff `fmtTs`); `—` when missing. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Signed money text with a true minus, e.g. `+12.30 USD`. */
export function formatSignedUsd(v: number): string {
  return `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)} USD`
}

// ---- Run metrics (spec 038 workstream D, echo-core 0.64.0) ----------------

/** One metric cell's text, with an optional tooltip. A missing value is `—`. */
export interface MetricText {
  text: string
  title?: string
}

const DASH = '—'
const usable = (v: number | null | undefined): v is number => v !== null && v !== undefined && Number.isFinite(v)
const grouped = (n: number, digits: number) =>
  Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
const signed = (n: number, body: string) => (n < 0 && Number(body.replace(/,/g, '')) !== 0 ? `−${body}` : body)

/** `10,000.00` / `−412.50`: en-US grouping, two decimals, no currency symbol. */
export function formatMetricMoney(v: number | null | undefined): MetricText {
  if (!usable(v)) return { text: DASH }
  return { text: signed(v, grouped(v, 2)) }
}

/** `61.5%`: one decimal; the value is already in percent units. */
export function formatMetricPct(v: number | null | undefined): MetricText {
  if (!usable(v)) return { text: DASH }
  return { text: `${signed(v, grouped(v, 1))}%` }
}

/** `12.3%` with the drawdown tooltip. */
export function formatMaxDrawdownPct(v: number | null | undefined): MetricText {
  if (!usable(v)) return { text: DASH }
  return { text: `${signed(v, grouped(v, 1))}%`, title: 'Largest fall from a peak, percent of the peak' }
}

/** Two decimals; a null value is `—`, with "No losing trades" when there were wins and no losses (Q16). */
export function formatProfitFactor(
  v: number | null | undefined,
  wins: number | null | undefined,
  losses: number | null | undefined,
): MetricText {
  if (!usable(v)) {
    return usable(wins) && usable(losses) && losses === 0 && wins > 0
      ? { text: DASH, title: 'No losing trades' }
      : { text: DASH }
  }
  return { text: signed(v, grouped(v, 2)) }
}

/** Two decimals; the tooltip names the basis verbatim, or says it was not recorded. */
export function formatSharpe(v: number | null | undefined, basis: string | null | undefined): MetricText {
  const title = typeof basis === 'string' && basis.trim() !== '' ? `Sharpe basis: ${basis}` : 'Basis not recorded'
  if (!usable(v)) return { text: DASH, title }
  return { text: signed(v, grouped(v, 2)), title }
}

/** `34 / 21`; `—` when either count is missing. */
export function formatWinsLosses(wins: number | null | undefined, losses: number | null | undefined): MetricText {
  if (!usable(wins) || !usable(losses)) return { text: DASH }
  return { text: `${Math.round(wins)} / ${Math.round(losses)}` }
}

function isoOf(epochSeconds: number | null | undefined): string | null {
  if (!usable(epochSeconds)) return null
  const d = new Date(epochSeconds * 1000)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/** UTC `2026-01-05 to 2026-03-28` from epoch seconds; a null side is `—`; the tooltip carries the full ISO times. */
export function formatTradeRange(first: number | null | undefined, last: number | null | undefined): MetricText {
  const a = isoOf(first)
  const b = isoOf(last)
  if (a === null && b === null) return { text: DASH }
  const text = `${a === null ? DASH : a.slice(0, 10)} to ${b === null ? DASH : b.slice(0, 10)}`
  return { text, title: `${a ?? DASH} to ${b ?? DASH}` }
}
